import { useState } from 'react';
import { Link } from 'react-router-dom';
import { describeTerms } from '@affily/shared/commission';
import { formatCount } from '@affily/shared/money';
import { api } from '../../lib/api.js';
import { useFetch } from '../../hooks/useFetch.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { TransparencyBadge } from '../../components/Transparency.jsx';
import { TrackingLink } from '../../components/TrackingLink.jsx';
import { Button, Badge, Spinner, EmptyState, Alert } from '../../components/ui.jsx';

const statusTone = {
  pending: 'caution',
  accepted: 'positive',
  rejected: 'neutral',
  withdrawn: 'neutral',
};

/**
 * Applications, for either side.
 *
 * Whoever did NOT initiate is the one who decides — a brand acts on a creator's
 * application, a creator acts on a brand's invitation — so the action column is
 * driven by `direction`, not by which dashboard you're standing in.
 */
export default function Applications({ side }) {
  const { data, loading, reload } = useFetch(`/applications?side=${side}`);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  const applications = data?.applications ?? [];
  const pending = applications.filter((a) => a.status === 'pending');

  async function decide(id, decision) {
    setBusyId(id);
    setError('');
    try {
      await api.post(`/applications/${id}/decide`, { decision });
      reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  }

  async function withdraw(id) {
    setBusyId(id);
    setError('');
    try {
      await api.post(`/applications/${id}/withdraw`);
      reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <DashboardLayout
      side={side}
      title="Applications"
      description={
        side === 'brand'
          ? 'Creators who applied, and invitations you sent.'
          : 'Programs you applied to, and invitations you received.'
      }
    >
      <Alert className="mb-4">{error}</Alert>

      {loading ? (
        <Spinner />
      ) : applications.length === 0 ? (
        <EmptyState
          title="Nothing here yet"
          action={
            side === 'brand' ? (
              <Button to="/dashboard/brand/creators">Find creators</Button>
            ) : (
              <Button to="/programs">Browse programs</Button>
            )
          }
        >
          {side === 'brand'
            ? 'Applications land here once creators find your program.'
            : 'You can read any program’s full terms before applying.'}
        </EmptyState>
      ) : (
        <div className="space-y-6">
          {pending.length > 0 && (
            <p className="text-sm text-ink-muted">
              {pending.length} awaiting a decision.
            </p>
          )}

          <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{side === 'brand' ? 'Creator' : 'Program'}</th>
                  <th>{side === 'brand' ? 'Program' : 'Terms'}</th>
                  <th>Message</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {applications.map((a) => {
                  // The counterparty acts. If you started it, you can only withdraw.
                  const iDecide =
                    side === 'brand'
                      ? a.direction === 'creator_applied'
                      : a.direction === 'brand_invited';

                  return (
                    <tr key={a.id}>
                      <td className="max-w-xs">
                        {side === 'brand' ? (
                          a.creator ? (
                            <>
                              <Link
                                to={`/creators/${a.creator.slug}`}
                                className="font-medium text-ink hover:text-accent"
                              >
                                {a.creator.displayName}
                              </Link>
                              <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
                                <span className="text-xs text-ink-muted">
                                  {formatCount(a.creator.totalFollowers)} followers
                                </span>
                                <TransparencyBadge
                                  transparency={a.creator.transparency}
                                  size="sm"
                                  showDetail={false}
                                />
                              </span>
                            </>
                          ) : (
                            <span className="text-ink-muted">Creator removed</span>
                          )
                        ) : a.program ? (
                          <>
                            <Link
                              to={`/programs/${a.program.slug}`}
                              className="font-medium text-ink hover:text-accent"
                            >
                              {a.program.title}
                            </Link>
                            {a.brand && (
                              <span className="block text-xs text-ink-muted">
                                {a.brand.companyName}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-ink-muted">Program removed</span>
                        )}
                      </td>

                      <td>
                        {side === 'brand' ? (
                          a.program && (
                            <Link
                              to={`/programs/${a.program.slug}`}
                              className="text-sm hover:text-accent"
                            >
                              {a.program.title}
                            </Link>
                          )
                        ) : a.program ? (
                          <span className="font-medium tabular-nums">
                            {describeTerms(a.program)}
                          </span>
                        ) : null}
                        <span className="block text-xs text-ink-faint">
                          {a.direction === 'creator_applied' ? 'Creator applied' : 'Brand invited'}
                        </span>
                      </td>

                      <td className="max-w-sm">
                        <span className="block truncate text-sm text-ink-muted" title={a.message ?? ''}>
                          {a.message || '—'}
                        </span>
                      </td>

                      <td>
                        <Badge tone={statusTone[a.status]}>{a.status}</Badge>
                        <span className="block text-xs text-ink-faint">
                          {new Date(a.createdAt).toLocaleDateString()}
                        </span>
                        {/* An accepted application carries its link, so the creator
                            can copy it here instead of hunting for another page. */}
                        {side === 'creator' && a.partnership?.trackingLinks?.[0] && (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <TrackingLink url={a.partnership.trackingLinks[0].url} />
                            {a.partnership.promoCode && (
                              <TrackingLink url={a.partnership.promoCode} noun="code" />
                            )}
                            <Link
                              to="/dashboard/creator/partnerships"
                              className="text-xs text-accent hover:underline"
                            >
                              View partnership
                            </Link>
                          </div>
                        )}
                      </td>

                      <td>
                        {a.status === 'pending' && iDecide && (
                          <span className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => decide(a.id, 'accept')}
                              disabled={busyId === a.id}
                            >
                              Accept
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => decide(a.id, 'reject')}
                              disabled={busyId === a.id}
                            >
                              Decline
                            </Button>
                          </span>
                        )}
                        {a.status === 'pending' && !iDecide && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => withdraw(a.id)}
                            disabled={busyId === a.id}
                          >
                            Withdraw
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-ink-faint">
            Accepting freezes the program’s terms onto the partnership. Later edits to the program
            never change commissions earned under it.
          </p>
        </div>
      )}
    </DashboardLayout>
  );
}
